import { FileImageOutlined, VideoCameraOutlined } from "@ant-design/icons";
import { gql, useMutation, useQuery } from "@apollo/client";
import { Alert, Button, Card, Col, Form, Input, Layout, PageHeader, Row, Select, Space, Switch } from "antd";
import { FormInstance } from "antd/lib/form";
import TextArea from "antd/lib/input/TextArea";
import Dragger from "antd/lib/upload/Dragger";
import { CenterResult } from "components/CenterResult";
import { CenterSpinner } from "components/CenterSpinner";
import { capitalize, first, last, omit } from "lodash";
import React, { Fragment, ReactElement, ReactNode, useCallback, useState } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";

const articleItemQuery = gql`
  query(
    $id: ID!
    $pagination: ConnectionPagination
    $collectionBrandSorting: CollectionBrandSorting
    $measureSizeGroupSorting: MeasureSizeGroupSorting
    $measureFittingSorting: MeasureFittingSorting
    $materialCompositionSorting: MaterialCompositionSorting
    $variantColorSorting: VariantColorSorting
    $varietyKindSorting: VarietyKindSorting
    $searchQuery: String
  ) {
    articleItem(id: $id) {
      id
      available
      nameIt
      nameEn
      brand {
        id
        nameEn
      }
      season {
        id
        nameEn
        kindEn
      }
      line {
        id
        nameEn
      }
      sizeGroup {
        id
        sizes {
          id
          code
          value
        }
      }
      fitting {
        id
        code
      }
      externalComposition {
        id
        nameEn
      }
      kind {
        id
        nameEn
      }
      shortDescriptionIt
      shortDescriptionEn
      longDescriptionIt
      longDescriptionEn
      modelInfoIt
      modelInfoEn
    }
    collectionBrands(pagination: $pagination, sorting: $collectionBrandSorting, searchQuery: $searchQuery) {
      nodes {
        id
        nameEn
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        page
        size
      }
      totalCount
      totalPages
    }

    measureSizeGroups(pagination: $pagination, sorting: $measureSizeGroupSorting, searchQuery: $searchQuery) {
      nodes {
        id
        sizes {
          id
          code
          value
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        page
        size
      }
      totalCount
      totalPages
    }
    measureFittings(pagination: $pagination, sorting: $measureFittingSorting, searchQuery: $searchQuery) {
      nodes {
        id
        code
        value
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        page
        size
      }
      totalCount
      totalPages
    }
    materialCompositions(pagination: $pagination, sorting: $materialCompositionSorting, searchQuery: $searchQuery) {
      nodes {
        id
        code
        nameEn
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        page
        size
      }
      totalCount
      totalPages
    }
    variantColors(pagination: $pagination, sorting: $variantColorSorting, searchQuery: $searchQuery) {
      nodes {
        id
        code
        nameEn
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        page
        size
      }
      totalCount
      totalPages
    }
    varietyKinds(pagination: $pagination, sorting: $varietyKindSorting, searchQuery: $searchQuery) {
      nodes {
        id
        nameEn
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        page
        size
      }
      totalCount
      totalPages
    }
  }
`;

const brandQuery = gql`
  query($brandId: ID!) {
    collectionBrand(id: $brandId) {
      seasons {
        id
        nameEn
        kindEn
      }
      lines {
        id
        nameEn
      }
    }
  }
`;

type BrandQueryType = (props: { children: (data: any) => ReactNode; variables: any }) => ReactElement;

const BrandQuery: BrandQueryType = (props) => {
  const { children, variables } = props;

  const { error, loading, data } = useQuery(brandQuery, {
    variables,
    fetchPolicy: "cache-and-network",
  });

  if (loading) return <CenterSpinner size="large" />;
  if (error || !data) return <>NOT FOUND</>;

  return <Fragment>{children(data.collectionBrand)}</Fragment>;
};

const createArticleItemMutation = gql`
  mutation($input: CreateArticleItemInput!) {
    createArticleItem(input: $input) {
      item {
        id
      }
    }
  }
`;

interface FieldData {
  name: string;
  errors: string[];
}

export const New: React.FC<{}> = () => {
  const [form] = Form.useForm();
  const [otherErrors, setOtherErrors] = useState<string[]>([]);
  const { id } = useParams();
  const navigate = useNavigate();

  const { error, loading: queryLoading, data } = useQuery(articleItemQuery, {
    variables: { id },
    fetchPolicy: "cache-and-network",
  });

  const [execMutation, { loading }] = useMutation(createArticleItemMutation, {
    errorPolicy: "none",
    onError: ({ graphQLErrors }) => {
      const fieldsErrors: FieldData[] = [];
      const otherErrors: string[] = [];
      graphQLErrors?.forEach((graphQLError) => {
        if (graphQLError.name) {
          fieldsErrors.push({ name: graphQLError.name, errors: (graphQLError as any).messages });
        } else {
          otherErrors.push(graphQLError.message);
        }
      });
      form.setFields(fieldsErrors);
      setOtherErrors(otherErrors);
    },
    onCompleted: (data) => {
      navigate("../");
    },
  });

  const valuesChangeHandler = useCallback(
    (changedValues) => {
      const fieldValues: any[] = [];
      if (changedValues.brandId) {
        fieldValues.push({ name: ["seasonId"], value: null });
        fieldValues.push({ name: ["lineId"], value: null });
      }
      if (changedValues.cover && changedValues.cover.file.status === "removed") {
        fieldValues.push({ name: ["cover"], value: null });
      }
      if (changedValues.mainVideo && changedValues.mainVideo.file.status === "removed") {
        fieldValues.push({ name: ["mainVideo"], value: null });
      }
      form.setFields(fieldValues);
    },
    [form],
  );

  const onFinish = (values: any) => {
    const input = {
      ...omit(values, ["cover", "mainVideo"]),
      cover: values.cover?.file,
      mainVideo: values.mainVideo?.file,
      referenceItemId: id,
    };
    execMutation({ variables: { input } });
  };

  if (queryLoading) return <CenterSpinner size="large" />;
  if (error)
    return (
      <CenterResult
        status="404"
        title="404"
        subTitle="Sorry, the page you visited does not exist."
        extra={
          <NavLink to="../">
            <Button type="primary">Back to list</Button>
          </NavLink>
        }
      />
    );

  const {
    collectionBrands: { nodes: collectionBrands },
    measureSizeGroups: { nodes: measureSizeGroups },
    measureFittings: { nodes: measureFittings },
    materialCompositions: { nodes: materialCompositions },
    variantColors: { nodes: variantColors },
    varietyKinds: { nodes: varietyKinds },
  } = data;

  const item = data.articleItem;
  const initialValues = {
    ...item,
    brandId: item?.brand?.id,
    seasonId: item?.season?.id,
    lineId: item?.line?.id,
    sizeGroupId: item?.sizeGroup?.id,
    fittingId: item?.fitting?.id,
    externalCompositionId: item?.externalComposition?.id,
    kindId: item?.kind?.id,
  };

  const uploadProps = {
    maxCount: 1,
    accept: "image/*",
    listType: "picture-card",
    beforeUpload: () => {
      return false;
    },
  };

  return (
    <Fragment>
      <PageHeader
        ghost={true}
        title="New Variant"
        extra={
          <Row gutter={[24, 0]}>
            <Col>
              <NavLink to="../">
                <Button>Cancel</Button>
              </NavLink>
            </Col>
            <Col>
              <Button
                type="primary"
                loading={loading}
                onClick={() => {
                  form.submit();
                }}
              >
                Save
              </Button>
            </Col>
          </Row>
        }
      />

      <Layout.Content className="content">
        <Card>
          {otherErrors.length > 0 && (
            <Space direction="vertical" style={{ marginBottom: "10px" }}>
              {otherErrors.map((otherError, index) => (
                <Alert key={index} type="error" message="Error" description={otherError} />
              ))}
            </Space>
          )}
          <Form
            layout="vertical"
            form={form}
            initialValues={initialValues}
            onValuesChange={valuesChangeHandler}
            onFinish={onFinish}
          >
            <Row gutter={[24, 0]}>
              <Col span={20}>
                <Form.Item name="code" label="Code" rules={[{ required: true, message: "is required" }]}>
                  <Input />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item name="available" label="Available" valuePropName={"checked"}>
                  <Switch />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="nameIt" label="Name IT" rules={[{ required: true, message: "is required" }]}>
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="nameEn" label="Name EN" rules={[{ required: true, message: "is required" }]}>
                  <Input />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="brandId" label="Brand">
                  <Select disabled>
                    {collectionBrands.map((brand: any) => (
                      <Select.Option key={brand.id} value={brand.id}>
                        {brand.nameEn}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={16}>
                <Form.Item dependencies={["brandId"]}>
                  {(form: FormInstance) => (
                    <BrandQuery variables={{ brandId: form.getFieldValue("brandId") }}>
                      {(data) => (
                        <Row gutter={[24, 0]}>
                          <Col span={12}>
                            <Form.Item name="seasonId" label="Season">
                              <Select disabled>
                                {data.seasons?.map((season: any) => (
                                  <Select.Option key={season.id} value={season.id}>
                                    {season.nameEn} - {season.kindEn}
                                  </Select.Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item name="lineId" label="Line">
                              <Select disabled>
                                {data.lines?.map((line: any) => (
                                  <Select.Option key={line.id} value={line.id}>
                                    {line.nameEn}
                                  </Select.Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </Col>
                        </Row>
                      )}
                    </BrandQuery>
                  )}
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="sizeGroupId" label="Size Group">
                  <Select disabled>
                    {measureSizeGroups.map((sg: any) => (
                      <Select.Option key={sg.id} value={sg.id}>
                        {first(sg.sizes).code} - {last(sg.sizes).code}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="fittingId" label="Fitting">
                  <Select disabled>
                    {measureFittings.map((fitting: any) => (
                      <Select.Option key={fitting.id} value={fitting.id}>
                        {capitalize(fitting.code)}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="externalCompositionId" label="External Composition">
                  <Select disabled>
                    {materialCompositions.map((composition: any) => (
                      <Select.Option key={composition.id} value={composition.id}>
                        <b>{composition.code}</b> - {composition.nameEn}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="colorId" label="Color" rules={[{ required: true, message: "is required" }]}>
                  <Select>
                    {variantColors.map((color: any) => (
                      <Select.Option key={color.id} value={color.id}>
                        <b>{color.code}</b> - {color.nameEn}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="kindId" label="Kind">
                  <Select disabled>
                    {varietyKinds.map((kind: any) => (
                      <Select.Option key={kind.id} value={kind.id}>
                        {kind.nameEn}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="shortDescriptionIt" label="Short Description IT">
                  <TextArea />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="shortDescriptionEn" label="Short Description En">
                  <TextArea />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="longDescriptionIt" label="Long Description IT">
                  <TextArea rows={6} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="longDescriptionEn" label="Long Description EN">
                  <TextArea rows={6} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="modelInfoIt" label="Model Info IT">
                  <TextArea />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="modelInfoEn" label="Model Info EN">
                  <TextArea />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="cover" label="Cover" rules={[{ required: true, message: "is required" }]}>
                  <Dragger {...uploadProps} defaultFileList={initialValues.cover}>
                    <div>
                      <FileImageOutlined style={{ fontSize: "30px" }} />
                      <div style={{ marginTop: 8 }}>Click or drop here</div>
                    </div>
                  </Dragger>
                </Form.Item>
              </Col>
              <Col span={8}></Col>
              <Col span={8}>
                <Form.Item name="mainVideo" label="Main Video">
                  <Dragger {...uploadProps} accept="video/*" defaultFileList={initialValues.mainVideo}>
                    <div>
                      <VideoCameraOutlined style={{ fontSize: "30px" }} />
                      <div style={{ marginTop: 8 }}>Click or drop here</div>
                    </div>
                  </Dragger>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Card>
      </Layout.Content>
    </Fragment>
  );
};
